import { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const PlayerContext = createContext(null);

export const usePlayer = () => {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error("usePlayer must be used within PlayerProvider");
  return ctx;
};

/**
 * PlayerProvider — owns the single <audio> element for the whole app so
 * playback survives route changes. Pages call playTrack(item) and the
 * GlobalAudioPlayer (rendered in Layout) shows the controls.
 */
export const PlayerProvider = ({ children }) => {
  const navigate = useNavigate();
  const { token: authToken } = useAuth();
  const audioRef = useRef(null);
  const [playingItem, setPlayingItem] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0..100
  const [repeat, setRepeat] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(null);
  // Optional queue (e.g., from playlist page) — pages register tracks for next/prev
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(null);

  // Re-fetch subscription status whenever the auth token changes so the
  // fade-out logic doesn't lock out real subscribers who logged in AFTER
  // PlayerContext mounted. Without this dependency, `hasSubscription` gets
  // fixed to `false` at page load (before login) and never refreshes — real
  // paying users then hit the 30-second fade-out despite having full access.
  const refreshSubStatus = useCallback(async () => {
    if (!authToken) {
      setHasSubscription(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/subscription/status`);
      setHasSubscription(!!res.data?.has_subscription);
    } catch {
      setHasSubscription(false);
    }
  }, [authToken]);

  useEffect(() => {
    refreshSubStatus();
  }, [refreshSubStatus]);

  const resolveStreamUrl = (rawUrl) => {
    if (!rawUrl) return rawUrl;
    if (rawUrl.startsWith("http")) return rawUrl;
    const baseUrl = API.replace("/api", "");
    if (rawUrl.startsWith("/api/")) return `${baseUrl}${rawUrl}`;
    return `${baseUrl}${rawUrl}`;
  };

  const playTrack = useCallback(async (item, opts = {}) => {
    // Same track: just toggle
    if (playingItem?.content_id === item.content_id && audioRef.current) {
      if (audioRef.current.paused) audioRef.current.play();
      else audioRef.current.pause();
      return;
    }

    try {
      const res = await axios.get(`${API}/content/stream/${item.content_id}`);
      const streamUrl = resolveStreamUrl(res.data.stream_url);
      setPlayingItem({ ...item, streamUrl });
      if (opts.queue && Array.isArray(opts.queue)) {
        setQueue(opts.queue);
        setQueueIndex(opts.queueIndex ?? 0);
      } else if (!opts.preserveQueue) {
        setQueue([]);
        setQueueIndex(null);
      }
      // Refresh sub status (limits may have changed)
      refreshSubStatus();
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error("Monthly limit reached. Upgrade or buy credits.");
      } else if (err.response?.status === 403) {
        toast.info("Subscribe to access this content");
        navigate("/subscribe");
      } else if (err.response?.status === 401) {
        toast.info("Please sign in to play this");
        navigate("/login");
      } else {
        toast.error("Failed to play content");
      }
      throw err;
    }
  }, [playingItem, refreshSubStatus, navigate]);

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    if (audioRef.current.paused) audioRef.current.play();
    else audioRef.current.pause();
  }, []);

  const seekToPercent = useCallback((pct) => {
    if (!audioRef.current || !audioRef.current.duration) return;
    audioRef.current.currentTime = (pct / 100) * audioRef.current.duration;
    setProgress(pct);
  }, []);

  const rewind15 = useCallback(() => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 15);
  }, []);

  const forward15 = useCallback(() => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.min(
      audioRef.current.duration || 0,
      audioRef.current.currentTime + 15
    );
  }, []);

  const closePlayer = useCallback(() => {
    if (audioRef.current) audioRef.current.pause();
    setPlayingItem(null);
    setIsPlaying(false);
    setProgress(0);
    setQueue([]);
    setQueueIndex(null);
  }, []);

  const playNextInQueue = useCallback(() => {
    if (!queue.length || queueIndex == null) return false;
    const next = (queueIndex + 1) % queue.length;
    setQueueIndex(next);
    playTrack(queue[next], { preserveQueue: true });
    return true;
  }, [queue, queueIndex, playTrack]);

  const playPrevInQueue = useCallback(() => {
    if (!queue.length || queueIndex == null) return false;
    const prev = queueIndex === 0 ? queue.length - 1 : queueIndex - 1;
    setQueueIndex(prev);
    playTrack(queue[prev], { preserveQueue: true });
    return true;
  }, [queue, queueIndex, playTrack]);

  // Audio element event handlers — encapsulated here so the element is owned by the provider
  const listenRef = useRef({ id: null, last: 0, played: 0, reported: false });
  const trackListen = (a) => {
    const L = listenRef.current;
    if (L.id !== playingItem?.content_id) Object.assign(L, { id: playingItem?.content_id, last: 0, played: 0, reported: false });
    const delta = a.currentTime - L.last;
    if (delta > 0 && delta < 1.5) L.played += delta;
    L.last = a.currentTime;
    if (!L.reported && hasSubscription && L.id && L.played >= a.duration * 0.9) {
      L.reported = true;
      axios.post(`${API}/quizzes/${L.id}/listened`).then(() => {
        window.dispatchEvent(new CustomEvent("barsongz:listened", { detail: L.id }));
      }).catch(() => { L.reported = false; });
    }
  };

  const handleTimeUpdate = () => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const t = a.currentTime;
    setProgress((t / a.duration) * 100);
    trackListen(a);

    // Non-subscriber fade-out at 30s, hard stop at 40s (matches existing behavior)
    if (hasSubscription === false) {
      if (t >= 30 && t < 40) {
        const fade = (t - 30) / 10;
        a.volume = Math.max(0, 1 - fade);
      } else if (t >= 40) {
        a.pause();
        a.currentTime = 0;
        a.volume = 1;
        setProgress(0);
        setIsPlaying(false);
        toast.info("Preview ended. Subscribe for full access!", {
          action: { label: "Subscribe", onClick: () => navigate("/subscribe") },
        });
      } else {
        a.volume = 1;
      }
    }
  };

  const handleEnded = () => {
    if (repeat) {
      audioRef.current?.play();
      return;
    }
    // Try queue next; otherwise stop
    if (!playNextInQueue()) {
      setIsPlaying(false);
      setProgress(0);
    }
  };

  const value = {
    audioRef,
    playingItem,
    isPlaying,
    progress,
    repeat,
    setRepeat,
    queue,
    queueIndex,
    playTrack,
    togglePlay,
    seekToPercent,
    rewind15,
    forward15,
    closePlayer,
    playNextInQueue,
    playPrevInQueue,
    hasSubscription,
    refreshSubStatus,
  };

  return (
    <PlayerContext.Provider value={value}>
      {children}
      {/* The single, app-wide audio element. Persists across route changes
          because PlayerProvider lives above <Routes>. */}
      {playingItem && (
        <audio
          ref={audioRef}
          src={playingItem.streamUrl}
          autoPlay
          loop={repeat && (!queue.length || queueIndex == null)}
          className="hidden"
          controlsList="nodownload noplaybackrate"
          onContextMenu={(e) => e.preventDefault()}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
          onError={(e) => {
            console.error("Audio error:", e.target.error);
            toast.error("Failed to load audio");
          }}
        />
      )}
    </PlayerContext.Provider>
  );
};
